import { StepCardContent } from './StepCard'
import { Headset, Bot, Tags, Shapes, LucideIcon } from 'lucide-react'
import { UseCaseCard } from './UseCaseCard'
import { BotUseCase } from '@bpinternal/const'
import { useEffect, useRef } from 'react'
import { getUseBotOnboardingStore } from '../../stores/useBotOnboardingStore'
import { useStepNavStore } from '../../stores/useStepNavStore'

export type UseCase = { title: string; description: string; icon: LucideIcon }

export const useCases: Record<BotUseCase, UseCase> = {
  'customer-support': {
    title: 'Customer Support',
    description: 'Fix problems and help users along the way',
    icon: Headset,
  },
  'brand-assistant': {
    title: 'AI Assistant',
    description: 'Help customers with anything about your brand',
    icon: Bot,
  },
  'product-recommendation': {
    title: 'Sales',
    description: 'Help customers discover the right products for them',
    icon: Tags,
  },
  other: {
    title: 'Other',
    description: 'Shape your agent’s unique personality',
    icon: Shapes,
  },
}

type Props = {
  workspaceId: string
  botId: string
}

export const BotUseCaseStep = ({ workspaceId, botId }: Props) => {
  const useBotOnboardingStore = getUseBotOnboardingStore({ botId, workspaceId })
  const setUseCaseId = useBotOnboardingStore((state) => state.setUseCaseId)
  const useCaseId = useBotOnboardingStore((state) => state.useCaseId)
  const setNextDisabled = useStepNavStore((state) => state.setNextDisabled)
  const setProgressBarStep = useStepNavStore((state) => state.setProgressBarStep)
  const selectedUseCaseRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setProgressBarStep(2)
    selectedUseCaseRef.current?.focus()
  }, [])

  useEffect(() => {
    setNextDisabled(!useCaseId)
  }, [useCaseId])

  return (
    <StepCardContent
      className="flex flex-1 flex-col justify-center"
      title="Goal"
      description="Select your bot’s primary goal so we can tailor its behavior."
    >
      <div className="grid grid-cols-2 gap-6">
        {Object.entries(useCases).map(([key, useCase]) => {
          return (
            <div
              key={key}
              ref={useCaseId === key ? selectedUseCaseRef : null}
              tabIndex={0}
              className={`flex cursor-pointer rounded-lg hover:bg-neutral-100 ${useCaseId === key ? 'bg-neutral-100 outline-1 outline-neutral-200 outline-solid' : 'bg-neutral-50'}`}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  setUseCaseId(key as BotUseCase)
                }
              }}
              onClick={() => {
                setUseCaseId(key as BotUseCase)
              }}
            >
              <UseCaseCard useCase={useCase} />
            </div>
          )
        })}
      </div>
    </StepCardContent>
  )
}
